import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, Loader2, Upload, X } from "lucide-react";
import { GetCargoDescFromHBL } from "@/redux/Common/actions";

const getRowKey = (item, index) =>
    `${item?.CONTAINER_NUMBER ?? ""}-${item?.SEAL_NUMBER ?? ""}-${item?.LINE_SEQ_NO ?? index}-${index}`;

const ImportContainersFromHBLModal = ({
    open,
    onClose,
    bookingId,
    companyId,
    fileRef = "",
    onImport,
}) => {
    const [step, setStep] = useState(1);
    const [cargoList, setCargoList] = useState([]);
    const [selectedKeys, setSelectedKeys] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const resetState = useCallback(() => {
        setStep(1);
        setCargoList([]);
        setSelectedKeys(new Set());
        setLoading(false);
        setError("");
    }, []);

    useEffect(() => {
        if (!open) resetState();
    }, [open, resetState]);

    const fetchCargoList = async () => {
        if (!bookingId) {
            setError("Booking ID is required to import from HBL.");
            return;
        }
        setLoading(true);
        setError("");
        try {
            const list = await GetCargoDescFromHBL(bookingId, companyId ?? 1);
            const items = Array.isArray(list) ? list : [];
            setCargoList(items);
            setSelectedKeys(new Set(items.map((item, index) => getRowKey(item, index))));
            setStep(2);
        } catch {
            setError("Failed to load cargo descriptions from HBL.");
        } finally {
            setLoading(false);
        }
    };

    const listWithKeys = useMemo(
        () => cargoList.map((item, index) => ({ item, key: getRowKey(item, index) })),
        [cargoList],
    );

    const allSelected =
        listWithKeys.length > 0 && listWithKeys.every(({ key }) => selectedKeys.has(key));

    const toggleSelectAll = () => {
        if (allSelected) {
            setSelectedKeys(new Set());
        } else {
            setSelectedKeys(new Set(listWithKeys.map(({ key }) => key)));
        }
    };

    const toggleSelect = (key) => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handleImport = () => {
        const selected = listWithKeys
            .filter(({ key }) => selectedKeys.has(key))
            .map(({ item }) => item);
        if (selected.length === 0) {
            setError("Select at least one container to import.");
            return;
        }
        onImport(selected);
        onClose();
    };

    if (!open) return null;

    const titleRef = fileRef ? `[${fileRef}]` : bookingId ? `[${bookingId}]` : "[]";

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                <div
                    className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                    onClick={onClose}
                    aria-hidden
                />
                <motion.div
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.2 }}
                    className="relative flex w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-slate-900"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-center justify-between bg-gradient-to-r from-slate-700 to-slate-800 px-5 py-3.5">
                        <h2 className="text-sm font-semibold text-white sm:text-base">
                            Import Containers From HBL&apos;s {titleRef}
                        </h2>
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-lg p-1 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {step === 1 ? (
                        <div className="space-y-5 p-6">
                            <div className="flex gap-3 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-950/30">
                                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
                                <p className="text-sm text-red-700 dark:text-red-300">
                                    <span className="font-semibold">Note:</span> Selected HBL containers
                                    will be added below your existing MBL container rows. Existing rows
                                    will not be removed.
                                </p>
                            </div>
                            {error && (
                                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                            )}
                            <div className="flex flex-wrap justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="rounded-lg bg-slate-800 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition-colors hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600"
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    onClick={fetchCargoList}
                                    disabled={loading}
                                    className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition-colors hover:bg-sky-700 disabled:opacity-60"
                                >
                                    {loading ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : null}
                                    Continue
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex max-h-[min(70vh,560px)] flex-col">
                            <div className="border-b border-slate-200 px-5 py-3 dark:border-slate-700">
                                <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        onChange={toggleSelectAll}
                                        className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                                    />
                                    Select all ({listWithKeys.length})
                                </label>
                            </div>

                            <div className="flex-1 overflow-y-auto p-4">
                                {loading ? (
                                    <div className="flex items-center justify-center py-12 text-slate-500">
                                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                        Loading containers...
                                    </div>
                                ) : listWithKeys.length === 0 ? (
                                    <p className="py-8 text-center text-sm text-slate-500">
                                        No cargo descriptions found for this booking.
                                    </p>
                                ) : (
                                    <div className="space-y-3">
                                        {listWithKeys.map(({ item, key }) => {
                                            const checked = selectedKeys.has(key);
                                            return (
                                                <label
                                                    key={key}
                                                    className={`flex cursor-pointer gap-3 rounded-lg border p-3 transition-colors ${
                                                        checked
                                                            ? "border-sky-300 bg-sky-50/80 dark:border-sky-700 dark:bg-sky-950/30"
                                                            : "border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50 dark:hover:bg-slate-800"
                                                    }`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => toggleSelect(key)}
                                                        className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                                                    />
                                                    <div className="min-w-0 flex-1 space-y-2">
                                                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                                                            <div>
                                                                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                                                    Container #
                                                                </p>
                                                                <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                                                                    {item.CONTAINER_NUMBER || "-"}
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                                                    Seal #
                                                                </p>
                                                                <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                                                                    {item.SEAL_NUMBER || "-"}
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                                                    No. of Pkg
                                                                </p>
                                                                <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                                                                    {item.NO_PACKAGE ?? "-"}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                                                Descriptions
                                                            </p>
                                                            <p className="line-clamp-4 whitespace-pre-wrap text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                                                                {item.DESCRIPTIONS || "-"}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </label>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {error && (
                                <p className="px-5 pb-2 text-sm text-red-600 dark:text-red-400">
                                    {error}
                                </p>
                            )}

                            <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3 dark:border-slate-700 dark:bg-slate-800/50">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="rounded-lg bg-slate-800 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition-colors hover:bg-slate-900 dark:bg-slate-700"
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    onClick={handleImport}
                                    disabled={loading || selectedKeys.size === 0}
                                    className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition-colors hover:bg-sky-700 disabled:opacity-60"
                                >
                                    <Upload className="h-3.5 w-3.5" />
                                    Import
                                </button>
                            </div>
                        </div>
                    )}
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default ImportContainersFromHBLModal;
